import { Injectable, inject } from '@angular/core';
import { take } from 'rxjs/operators';

import { Client } from 'src/providers/client/client';
import { Me } from 'src/providers/me/me';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { SubscriptionsQueriesService } from 'src/shared/services/subscriptions-queries.service';
import { SuccessPage } from 'src/shared/pages/success/success';

import { AddProsOnboardingComponent } from 'src/shared/components/onboarding/add-pros/add-pros.component';
import { NotifyProsOnboardingComponent } from 'src/shared/components/onboarding/notify-pros-onboarding/notify-pros-onboarding.component';
import { ListingsSetupComponent } from 'src/shared/components/listings-setup/listings-setup.component';
import { ConnectBankOnboardingComponent } from 'src/shared/components/onboarding/connect-bank-onboarding/connect-bank-onboarding.component';
import { ManageLockAssignmentsComponent } from 'src/shared/components/manage-lock-assignments/manage-lock-assignments.component';
import { LegalComplianceOnboardingComponent } from 'src/shared/components/onboarding/legal-compliance-onboarding/legal-compliance-onboarding.component';
import { ListingOptimizationOnboardingComponent } from 'src/shared/components/onboarding/listing-optimization-onboarding/listing-optimization-onboarding.component';
import { ChecklistsOnboardingComponent } from 'src/shared/components/onboarding/checklists-onboarding/checklists-onboarding.component';
import { GuidebookOnboardingComponent } from 'src/shared/components/onboarding/guidebook-onboarding/guidebook-onboarding.component';
import { RestockingInputsOnboardingComponent } from 'src/shared/components/onboarding/restocking-onboarding/restocking-onboarding.component';
import { PropertyPhotosOnboardingComponent } from 'src/shared/components/onboarding/property-photos-onboarding/property-photos-onboarding.component';
import { EditPricingPage } from 'src/pages/marketing/components/edit-pricing/edit-pricing.page';
import { EnableAutopilotComponent } from 'src/pages/go-live/enable-autopilot/enable-autopilot.component';

export interface StepRouteConfig {
  route: string;
  component: any;
  title: string;
}

/**
 * Single source of truth for routing a Funnel V2 onboarding `stepId` to its destination
 * component/route, including the subscription-gating detour (EnableAutopilotComponent) and
 * the connect-listings guard for the pricing / listing-opt steps.
 *
 * The route map was lifted from the (now deprecated) Getting Started page's `stepRoutes`
 * and `onNodeClick`; this service is the new home for that logic so the assigned-items
 * surface (`onboarding-status`) and any future caller share one canonical map. Note the
 * split: `pros` -> Add Pros (writes only `pros`), `notify` -> Notify Pros (writes only
 * `notify`), and the `restocking` inputs wizard.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingStepNavigationService {
  private client = inject(Client);
  private me = inject(Me);
  private storage = inject(TidyStorage);
  private rightSidePanelService = inject(RightSidePanelService);
  private subscriptionsQueries = inject(SubscriptionsQueriesService);

  // Tell Us About Your Listings and Add Property Photos are self-serve data-collection steps; every
  // other action item requires an active Autopilot subscription, so opening it detours
  // through the Subscribe page. The `subscribe` step IS that page, so it
  // must never detour through itself. Property photos are exempt because we're asking the
  // client for inputs (like Tell Us About Your Listings), not delivering a paid feature.
  private readonly SUBSCRIPTION_EXEMPT_STEPS = ['listings', 'subscribe', 'property-photos'];

  // Steps that need a connected listing first (pricing / listing optimization).
  private readonly LISTING_GATED_STEPS = ['pricing', 'listing-opt'];

  readonly stepRoutes: Record<string, StepRouteConfig> = {
    'listings': { route: 'listings-setup', component: ListingsSetupComponent, title: 'Tell Us About Your Listings' },
    'subscribe': { route: 'go-live/enable-autopilot', component: EnableAutopilotComponent, title: 'Subscribe' },
    'pros': { route: 'onboarding/addPros', component: AddProsOnboardingComponent, title: 'Add Your Pros' },
    'notify': { route: 'getting-started/notify-pros', component: NotifyProsOnboardingComponent, title: 'Notify Pros' },
    'guidebook': { route: 'getting-started/guidebook', component: GuidebookOnboardingComponent, title: 'Share Your Guidebook' },
    'legal': { route: 'getting-started/legal-compliance', component: LegalComplianceOnboardingComponent, title: 'Legal / Compliance' },
    'checklists': { route: 'getting-started/checklists', component: ChecklistsOnboardingComponent, title: 'Checklists' },
    'locks': { route: 'smart-locks/manage-assignments', component: ManageLockAssignmentsComponent, title: 'Connect Locks' },
    'listing-opt': { route: 'getting-started/listing-optimization', component: ListingOptimizationOnboardingComponent, title: 'Listing Optimization' },
    'bank': { route: 'getting-started/connect-bank', component: ConnectBankOnboardingComponent, title: 'Connect Bank' },
    'pricing': { route: 'edit-pricing', component: EditPricingPage, title: 'Pricing Optimization' },
    'restocking': { route: 'getting-started/restocking', component: RestockingInputsOnboardingComponent, title: 'Restocking' },
    'property-photos': { route: 'getting-started/property-photos', component: PropertyPhotosOnboardingComponent, title: 'Add Property Photos' },
  };

  /**
   * Opens the onboarding step for the given stepId + address, applying the listing guard
   * (pricing / listing-opt) and the subscription gate. `onClosed` runs once the dialog
   * closes so the caller can re-read state and flip checkmarks.
   */
  async openStep(stepId: string, addressId: number | undefined, onClosed?: () => void): Promise<void> {
    const stepConfig = this.stepRoutes[stepId];
    if (!stepConfig) { return; }

    // Listing guard: pricing / listing optimization need a connected listing first.
    if (this.LISTING_GATED_STEPS.includes(stepId)) {
      const guarded = await this.guardOnListing(stepId, addressId!, onClosed);
      if (guarded) { return; }
    }

    const params: any = { addressId, fromGettingStarted: true };
    // The `subscribe` step lands directly on the Enable Autopilot page (the subscribe page).
    // Flag it so that page shows a plain "Subscribe" header and marks this step complete on submit.
    if (stepId === 'subscribe') { params.fromSubscribeStep = true; }

    // Subscription gate: every step except Tell Us About Your Listings detours through "Subscribe to
    // Continue" (Enable Autopilot) when the plan lacks Autopilot.
    if (!this.SUBSCRIPTION_EXEMPT_STEPS.includes(stepId)) {
      const detoured = await this.guardOnSubscription(stepConfig, params, onClosed);
      if (detoured) { return; }
    }

    this.rightSidePanelService.setDialogPageTitle(stepConfig.title);
    await this.rightSidePanelService.navigateTo(stepConfig.route, params, stepConfig.component);
    this.subscribeOnClosed(onClosed);
  }

  // Returns true if the guard intercepted (a Success "Tell Us About Your Listings" prompt was shown).
  private async guardOnListing(stepId: string, addressId: number, onClosed?: () => void): Promise<boolean> {
    let listings: any[] = [];
    try {
      listings = await this.client.getAddressListings(addressId);
    } catch {
      listings = [];
    }
    if (listings?.length) { return false; }
    const header = stepId === 'pricing'
      ? 'Please connect your listing before optimizing your pricing'
      : 'Please connect your listing before optimizing your listing';
    const title = stepId === 'pricing' ? 'Pricing Optimization' : 'Listing Optimization';
    const successParams: any = {
      header,
      body: '',
      buttonText: 'Tell Us About Your Listings',
      customActionKey: 'navigateToConnectListings',
      addressId,
      allButtonsPrimary: true,
      fromGettingStarted: true,
    };
    this.rightSidePanelService.setDialogPageTitle(title);
    await this.rightSidePanelService.navigateTo('success', successParams, SuccessPage);
    this.subscribeOnClosed(onClosed);
    return true;
  }

  // Returns true if the user was detoured to Enable Autopilot.
  private async guardOnSubscription(stepConfig: StepRouteConfig, params: any, onClosed?: () => void): Promise<boolean> {
    let hasAutopilot = false;
    try {
      const subscriptions = await this.subscriptionsQueries.getSubscriptions();
      const currentSubscription = this.me.getCurrentSubscription(subscriptions);
      hasAutopilot = !!currentSubscription?.plan?.key?.includes('full-autopilot');
    } catch {
      hasAutopilot = false;
    }
    if (hasAutopilot) { return false; }
    await this.storage.save('autopilot-pending-step', {
      route: stepConfig.route,
      title: stepConfig.title,
      params,
    });
    this.rightSidePanelService.setDialogPageTitle('Enable Autopilot');
    await this.rightSidePanelService.navigateTo('go-live/enable-autopilot', { fromGettingStarted: true }, EnableAutopilotComponent);
    this.subscribeOnClosed(onClosed);
    return true;
  }

  private subscribeOnClosed(onClosed?: () => void): void {
    if (!onClosed) { return; }
    this.rightSidePanelService.afterPanelClosed().pipe(take(1)).subscribe(() => onClosed());
  }
}
